import { Grid, Button } from '@mui/material';
import * as Yup from 'yup';
import { useReactHookForm } from '../../hooks/useReactHookForm';
import { ReactHookForm } from '../../components/hook-form/react-hook-form.component';
import { FormInput } from '../../components/hook-form/form-input.component';
import { FormSelect } from '../../components/hook-form/form-select.component';
import { Note } from '../../models/note';
import { useSaveData } from '../../hooks/useSaveData';
import { noteApiKeyStore } from '../../api/note-api-key-store';
import { LoadingIndicator } from '../../components/loading-indicator.component';
import { useAuthorize } from '../../hooks/use-authorize';
import { UserPermissionName } from '../../models/user-permission-name';

interface NoteRequestArgs {
  projectID?: number;
  officeID?: number;
  customerID?: number;
  unitID?: number;
  noteTempEntityID?: number;
  planReviewTaskID?: number;
}

interface NoteCreateFromProps {
  noteRequestArgs?: NoteRequestArgs;
  note?: Note;
  onSaveComplete: (data: Note) => void;
  onClosed: () => void;
  showNoteTypes?: boolean;
}

const noteTypeOptions = [
  { value: 'Task Note', label: 'Task Note' },
  { value: 'Project Note', label: 'Project Note' },
];

const noteValidationSchemaWithType = Yup.object().shape({
  note: Yup.string().required('Note is required'),
  noteType: Yup.string().required('Note type is required'),
});

const noteValidationSchema = Yup.object().shape({
  note: Yup.string().required('Note is required'),
});

export const SaveNoteFrom = ({ note, noteRequestArgs, onSaveComplete, onClosed, showNoteTypes = false }: NoteCreateFromProps) => {
  const { isLoading, mutateAsync } = useSaveData<Note>(
    note ? noteApiKeyStore.updateNote() : noteApiKeyStore.createNote()
  );

  const form = useReactHookForm({
    defaultValues: { note: note?.note, noteType: note?.noteSection },
    yupSchema: showNoteTypes ? noteValidationSchemaWithType : noteValidationSchema,
  });

  const handleSubmit = async (formValues: any) => {
    const isTaskNote = formValues.noteType === 'Task Note';

    const resolvedArgs = {
      ...noteRequestArgs,
      unitID: isTaskNote ? null : noteRequestArgs?.unitID,
      planReviewTaskID: isTaskNote ? noteRequestArgs?.planReviewTaskID : null,
      noteSection: formValues.noteType,
      ...(note?.noteID ? { noteID: note.noteID } : {}),
    };
    const data = await mutateAsync({ data: { ...formValues, ...resolvedArgs } });
    onSaveComplete(data.data);
    form.reset({ note: '', noteSection: '', noteType: '' });
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <ReactHookForm formName="SEVE_FORM" onSubmit={handleSubmit} formContextProps={form}>
      <Grid container spacing={2} alignItems="center">
        <Grid item lg={10} sm={10} xl={10} xs={10}>
          <LoadingIndicator isLoading={isLoading} />
          {showNoteTypes && (
            <div style={{ width: '220px' }}>
              <FormSelect name="noteType" label="Note Type" options={noteTypeOptions} />
            </div>
          )}
          <div style={{ marginTop: '8px' }}>
            <FormInput name="note" label="Note" multiline={true} size={3.5} autoFocus />
          </div>
        </Grid>
        <Grid item lg={2} sm={2} xl={2} xs={2} sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <Button
            type="button"
            variant="contained"
            fullWidth
            onClick={() => form.handleSubmit(handleSubmit)()}
            disabled={isReadOnlyPermission}
          >
            Save
          </Button>
          <Button type="button" variant="outlined" fullWidth onClick={onClosed}>
            Cancel
          </Button>
        </Grid>
      </Grid>
    </ReactHookForm>
  );
};
